import React from 'react';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Pencil } from 'lucide-react';
import { ErrorState, PersonCell, TableCard, TableEmptyRow, TableSkeletonRows } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { formatCount, formatDays } from '../../../utils/format';
import { asSentence, suspensionEndDate } from './helpers';
import type { WeekendShiftRosterData, WeekendShiftRosterRow } from './types';

const COLUMNS = 6;

export interface SuspensionsTabProps {
  rosterData: WeekendShiftRosterData | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  suspendedReps: WeekendShiftRosterRow[];
  onOpenRep: (rep: WeekendShiftRosterRow) => void;
}

/** Reps currently blocked from weekend signups, soonest-ending first. */
const SuspensionsTab: React.FC<SuspensionsTabProps> = ({ rosterData, loading, error, onRetry, suspendedReps, onOpenRep }) => {
  const rows = [...suspendedReps].sort(
    (a, b) => (a.suspensionStatus.daysRemaining ?? 0) - (b.suspensionStatus.daysRemaining ?? 0) || a.name.localeCompare(b.name)
  );

  const renderRows = () => {
    if (!rosterData && loading) return <TableSkeletonRows columns={COLUMNS} rows={4} />;
    if (!rosterData && error) {
      return (
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell colSpan={COLUMNS} sx={{ borderBottom: 0 }}>
            <ErrorState compact title="Couldn’t load suspensions" body={asSentence(error)} onRetry={onRetry} />
          </TableCell>
        </TableRow>
      );
    }
    if (rows.length === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMNS}
          title="No reps are suspended"
          body="Reps who miss a required check-in, or whom you suspend, can’t sign up for weekend shifts for 60 days. They appear here until the suspension ends."
        />
      );
    }
    return rows.map((rep) => (
      <TableRow key={rep.employeeId} hover onClick={() => onOpenRep(rep)} sx={{ cursor: 'pointer' }}>
        <TableCell sx={{ minWidth: 220, maxWidth: 280 }}>
          <PersonCell name={rep.name} secondary={rep.email} />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{rep.teamName || 'Sales'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{suspensionEndDate(rep)}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDays(rep.suspensionStatus.daysRemaining, 0)}</TableCell>
        <TableCell sx={{ minWidth: 280 }}>
          <Typography variant="body2" sx={{ color: rep.suspensionStatus.lastViolationReason ? sbg.text.primary : sbg.text.secondary }}>
            {rep.suspensionStatus.lastViolationReason || 'No reason recorded'}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ width: 56 }}>
          <Tooltip title="Manage suspension">
            <IconButton
              size="small"
              aria-label={`Manage suspension for ${rep.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenRep(rep);
              }}
            >
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <TableCard
      aria-label="Suspended reps"
      toolbar={
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', width: '100%' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
              Suspended from signups
            </Typography>
            <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
              A missed check-in blocks new weekend signups for 60 days. Open a rep to lift a suspension early.
            </Typography>
          </Box>
          {rosterData && (
            <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
              {formatCount(rows.length, 'rep')}
            </Typography>
          )}
        </Box>
      }
    >
      <Table sx={{ minWidth: 860 }} aria-busy={loading || undefined}>
        <TableHead>
          <TableRow>
            <TableCell>Rep</TableCell>
            <TableCell>Team</TableCell>
            <TableCell>Suspended through</TableCell>
            <TableCell align="right">Time left</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell align="right">
              <span className="sbg-sr-only">Actions</span>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows()}</TableBody>
      </Table>
    </TableCard>
  );
};

export default SuspensionsTab;
